import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import './Gauge.css';
import { resolveValueColorFromStops } from './liquidShared';

const extractNumericValueFromDatastreams = (datastreams, pin) => {
  if (!datastreams || !pin) return undefined;

  if (typeof datastreams === 'object' && datastreams !== null) {
    if (Object.prototype.hasOwnProperty.call(datastreams, pin)) {
      const n = Number(datastreams[pin]);
      return Number.isFinite(n) ? n : undefined;
    }

    if (datastreams.pins && Object.prototype.hasOwnProperty.call(datastreams.pins, pin)) {
      const n = Number(datastreams.pins[pin]);
      return Number.isFinite(n) ? n : undefined;
    }

    if (datastreams.datastreams && Object.prototype.hasOwnProperty.call(datastreams.datastreams, pin)) {
      const n = Number(datastreams.datastreams[pin]);
      return Number.isFinite(n) ? n : undefined;
    }
  }

  return undefined;
};

const toFiniteNumberOrUndefined = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const resolveControlColor = (value) => {
  const normalized = String(value || '').trim();
  return normalized ? normalized : null;
};

const getSafeSvgId = (prefix, id) =>
  `${prefix}-${String(id || 'widget').replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const Gauge = ({ 
  title, 
  transparentBackground, 
  min, 
  max, 
  value, 
  unit, 
  showUnit = true,
  color,
  unfilledColor,
  startColor, 
  stopColor,
  dims, 
  draggable, 
  pin, 
  id, 
  colors, 
  colorGrading = false, 
  changeColorBasedOnValue = false, 
  decimals,
  datastreams,
  labelFontSize,
  valueFontSize,
}) => {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 300, height: 300 });
  const [cursorState, setCursorState] = useState('idle');

  const fontSizeMultiplier = typeof window !== 'undefined' 
    ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--font-size-multiplier') || 1)
    : 1;

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ 
          width: width, 
          height: height 
        });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const calculateRadius = useCallback(() => {
    const verticalChrome = 46 * fontSizeMultiplier;
    const horizontalPadding = 16 * fontSizeMultiplier;
    const availableHeight = Math.max(containerSize.height - verticalChrome, 50);
    const availableWidth = Math.max(containerSize.width - horizontalPadding, 50);
    return Math.max(24, Math.min(availableHeight, availableWidth / 2));
  }, [containerSize, fontSizeMultiplier]);

  const radius = calculateRadius();

  const resolvedValue = useMemo(() => {
    if (pin) {
      const fromStream = extractNumericValueFromDatastreams(datastreams, pin);
      if (fromStream !== undefined) return fromStream;
    }

    const direct = toFiniteNumberOrUndefined(value); 
    return direct;

  }, [value, datastreams, pin]);

  const datastreamMeta = useMemo(() => {
    const list = Array.isArray(datastreams) ? datastreams : [];
    if (!pin) return null;
    return list.find(ds => String(ds?.pin || '') === String(pin)) || null;
  }, [datastreams, pin]);

  const datastreamColor = useMemo(
    () => resolveControlColor(datastreamMeta?.color),
    [datastreamMeta]
  );

  const resolvedMin = useMemo(() => {
    if (datastreamMeta?.min !== undefined && datastreamMeta?.min !== null) return datastreamMeta.min;
    return min;
  }, [datastreamMeta, min]);

  const resolvedMax = useMemo(() => {
    if (datastreamMeta?.max !== undefined && datastreamMeta?.max !== null) return datastreamMeta.max;
    return max;
  }, [datastreamMeta, max]);

  const resolvedUnit = useMemo(() => {
    const dsUnit = datastreamMeta?.unit;
    if (dsUnit !== undefined && dsUnit !== null && String(dsUnit).trim() !== '') return dsUnit;
    return unit;
  }, [datastreamMeta, unit]);

  const minNum = useMemo(() => {
    const n = Number(resolvedMin);
    return Number.isFinite(n) ? n : 0;
  }, [resolvedMin]);

  const maxNum = useMemo(() => {
    const n = Number(resolvedMax);
    return Number.isFinite(n) ? n : 100;
  }, [resolvedMax]);

  const safeValueForMath = useMemo(() => {
    if (resolvedValue === undefined) return minNum;
    return Math.max(minNum, Math.min(maxNum, resolvedValue));
  }, [resolvedValue, minNum, maxNum]);

  const parameters = useMemo(() => {
    const minVal = minNum;
    const maxVal = maxNum;

    const valueConsidered = Math.max(minVal, Math.min(maxVal, safeValueForMath));
    const range = maxVal - minVal;

    const effectiveRadius = 0.8 * radius;
    const strokeWidth = radius - effectiveRadius;

    const GAUGE_ANGLE = 165; 
    const rotationAngle = 188; 

    const fullCircumference = 2 * Math.PI * effectiveRadius;
    const arcLength = (GAUGE_ANGLE / 360) * fullCircumference;

    const backgroundDasharray = `${arcLength} ${fullCircumference - arcLength}`;

    const normalizedValue = range > 0 ? (valueConsidered - minVal) / range : 0;

    const filledArcLength = normalizedValue * arcLength;
    const fillingDasharray = `${filledArcLength} ${fullCircumference - filledArcLength}`;

    const percentage = Math.round(normalizedValue * 100);

    const customTitleFontSize = Number(labelFontSize);
    const customValueFontSize = Number(valueFontSize);
    const titleFontSize = Number.isFinite(customTitleFontSize) && customTitleFontSize > 0
      ? customTitleFontSize * fontSizeMultiplier
      : clamp(radius * 0.082, 14 * fontSizeMultiplier, 18 * fontSizeMultiplier);
    const resolvedValueFontSize = Number.isFinite(customValueFontSize) && customValueFontSize > 0
      ? customValueFontSize * fontSizeMultiplier
      : clamp(radius * 0.15, 35 * fontSizeMultiplier, 36 * fontSizeMultiplier);
    const unitFontSize = Math.max(radius * 0.15, 12 * fontSizeMultiplier);
    const percentageFontSize = Math.max(radius * 0.15, 14 * fontSizeMultiplier);
    const minMaxFontSize = Math.max(radius * 0.25, 14 * fontSizeMultiplier);

    return {
      radius,
      innerRadius: effectiveRadius,
      strokeWidth,
      normalizedValue,
      percentage,
      backgroundDasharray,
      fillingDasharray,
      rotationAngle, 
      arcLength,
      filledArcLength,
      titleFontSize,
      valueFontSize: resolvedValueFontSize,
      unitFontSize,
      percentageFontSize,
      minMaxFontSize
    };
  }, [labelFontSize, minNum, maxNum, safeValueForMath, radius, fontSizeMultiplier, valueFontSize]);

  useEffect(() => {
    if (!containerRef.current) return;

    const el = containerRef.current;
    el.style.setProperty('--gauge-title-font-size', `${parameters.titleFontSize}px`);
    el.style.setProperty('--gauge-value-font-size', `${parameters.valueFontSize}px`);
    el.style.setProperty(
      '--gauge-minmax-font-size',
      `${Math.max(parameters.minMaxFontSize * 0.38, 11 * fontSizeMultiplier)}px`
    );
  }, [
    fontSizeMultiplier,
    parameters.minMaxFontSize,
    parameters.titleFontSize,
    parameters.valueFontSize,
  ]);

  const mouseEvent = useCallback((cursor) => {
    if (!draggable) return;
    setCursorState(cursor);
  }, [draggable]);

  const formattedValue = useMemo(() => {
    const v = resolvedValue === undefined ? undefined : safeValueForMath;
    if (v === undefined) return '-';
    if (!isNaN(v) && isFinite(v) && decimals != null) {
      return parseFloat(parseFloat(v).toFixed(decimals)).toString();
    }
    return v;
  }, [resolvedValue, safeValueForMath, decimals]);

  const getColorForValue = useCallback(() => {
    const v = resolvedValue === undefined ? minNum : safeValueForMath;
    const fixedColor = resolveControlColor(color) || datastreamColor || resolveControlColor(stopColor);
    return resolveValueColorFromStops({
      value: v,
      baseColor: fixedColor || 'var(--gauge-default-stop, #FF928A)',
      stops: colors,
      enabled: changeColorBasedOnValue,
      grading: colorGrading,
    }) || fixedColor || 'var(--gauge-default-stop, #FF928A)';
  }, [resolvedValue, safeValueForMath, minNum, colors, colorGrading, changeColorBasedOnValue, color, datastreamColor, stopColor]);

  const shouldUseLegacyFixedGradient =
    !changeColorBasedOnValue && colorGrading && !resolveControlColor(color) && !datastreamColor && (startColor || stopColor);
  const fillColor = useMemo(() => getColorForValue(), [getColorForValue]);
  const trackColor = resolveControlColor(unfilledColor);
  const fillGradientId = getSafeSvgId('grad', id);

  const cursorClass = draggable
    ? (cursorState === 'grabbing' ? 'gauge-widget--grabbing' : 'gauge-widget--grab')
    : 'gauge-widget--auto';

  return (
    <div 
      ref={containerRef}
      className={[
        "widget-container",
        "gauge-widget",
        cursorClass,
        transparentBackground ? "gauge-widget--transparent" : "",
      ].filter(Boolean).join(" ")}
      onMouseDown={() => mouseEvent('grabbing')}
      onMouseUp={() => mouseEvent('grab')}
      onMouseLeave={() => mouseEvent('grab')}
      draggable={draggable} 
      onDragStart={(e) => {
        const widgetId = e.target.closest('.widget').id;
        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;
        let dragData = `gauge;${widgetId};${offsetX};${offsetY};`;
        dragData += widgetId.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
    >
      <h3 className="gauge-widget__title">
        {title}
      </h3>

      <div className="gauge-widget__content">
        <div className="gauge-widget__svgWrap">
          <svg 
            className="gauge"
            width={radius * 2}
            height={radius}
            viewBox={`0 0 ${radius * 2} ${radius}`}
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              {shouldUseLegacyFixedGradient && (
                <linearGradient id={fillGradientId} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={startColor || stopColor || 'var(--gauge-default-stop, #FF928A)'} stopOpacity="1" />
                  <stop offset="100%" stopColor={stopColor || 'var(--gauge-default-stop, #FF928A)'} stopOpacity="1" />
                </linearGradient>
              )}
            </defs>

            <circle
              cx={parameters.radius}
              cy={parameters.radius}
              strokeWidth={parameters.strokeWidth}
              r={parameters.innerRadius}
              fill="transparent"
              stroke={trackColor || fillColor}
              strokeOpacity={trackColor ? 1 : 0.24}
              strokeDasharray={parameters.backgroundDasharray}
              strokeDashoffset="0"
              transform={`rotate(${parameters.rotationAngle}, ${parameters.radius}, ${parameters.radius})`}
              strokeLinecap="round"
            />

            <circle
              className="gauge-widget__fillArc"
              cx={parameters.radius}
              cy={parameters.radius}
              strokeWidth={parameters.strokeWidth}
              r={parameters.innerRadius}
              fill="transparent"
              stroke={
                shouldUseLegacyFixedGradient
                  ? `url(#${fillGradientId})`
                  : fillColor
              }
              strokeDasharray={parameters.fillingDasharray}
              strokeDashoffset="0"
              transform={`rotate(${parameters.rotationAngle}, ${parameters.radius}, ${parameters.radius})`}
              strokeLinecap="round"
            />

          </svg>

          <div className="gauge-widget__centerText">
            <div className="gauge-widget__valueRow">
              <span className="gauge-widget__valuePill">
                {formattedValue}
                {showUnit && resolvedUnit != null && String(resolvedUnit).trim() !== '' ? resolvedUnit : ''}
              </span>
            </div>
          </div>
        </div>

        <div className="gauge-widget__minMax">
          <span className="gauge-widget__minMaxText">
            {minNum}
          </span>
          <span className="gauge-widget__minMaxText">
            {maxNum}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Gauge;
